import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Collapse,
  Container
} from '@mui/material';
import {
  Search as SearchIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const OfflinePaymentForm = () => {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Debug: Log API URL on component mount
  useEffect(() => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    console.log('🚀 OfflinePaymentForm mounted');
    console.log('📊 Environment Variables:');
    console.log('- REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
    console.log('- NODE_ENV:', process.env.NODE_ENV);
    console.log('- Final API URL:', apiUrl);
    
    // Test API connectivity
    const testAPI = async () => {
      try {
        console.log('🔗 Testing API connectivity...');
        const testUrl = `${apiUrl}/accounts/health`;
        console.log('- Test URL:', testUrl);
        
        const response = await fetch(testUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        
        console.log('✅ API Health Check Response Status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ API Health Check Data:', data);
        } else {
          console.log('❌ API Health Check Failed - Response not OK');
        }
      } catch (error) {
        console.log('❌ API Health Check Failed - Network Error:', error);
        console.log('Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
      }
    };

    testAPI();
  }, []);

  // Search and Application State
  const [userName, setUserName] = useState('');
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState('');
  const [showApplications, setShowApplications] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  // Laboratory Details
  const [labDetails, setLabDetails] = useState({
    userName: '',
    category: '',
    orgId: '',
    appId: '',
    labTypeId: '',
    assessmentTypeId: ''
  });

  // Payment Types
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [selectedPaymentType, setSelectedPaymentType] = useState('');
  const [showPaymentDetails, setShowPaymentDetails] = useState(false);
  const [paymentAlreadyMade, setPaymentAlreadyMade] = useState(false);
  const [paymentAlreadyMadeDetails, setPaymentAlreadyMadeDetails] = useState(null);

  // Payment Details
  const [paymentDetails, setPaymentDetails] = useState({
    totalAmount: 0,
    amount:0,
    taxAmount: 0,
    taxLabel: 'Tax (18%):',
    grandTotal: 0,
    deductTds: false,
    tdsAmount: 0,
    tadaAmount: 0,
    tan: ''
  });

  // Offline Payment Details
  const [offlineDetails, setOfflineDetails] = useState({
    mode: '',
    refNo: '',
    transactionDate: ''
  });

  // Payment Form
  const [paymentForm, setPaymentForm] = useState({
    paymentMode: '',
    orderIdChequeNo: '',
    payeeBank: '',
    transactionDate: new Date(),
    amount: '',
    remarks: ''
  });

  // Previous Payments
  const [previousPayments, setPreviousPayments] = useState([]);
  const [showPreviousPayments, setShowPreviousPayments] = useState(false);

  // Payment Modes
  const paymentModes = [
    'Cheque',
    'Demand Draft',
    'Direct Payment',
    'HDFC Payment Gateway',
    'TDS Recovery',
    'Payment Through CC Avenue',
    'Previous Adjustments',
    'Virtual Bank Account Details',
    'Payment through Billdesk',
    'ICICI Payment gateway',
    'ICICI Portal'
  ];

  // API Functions
  const searchUser = async () => {
    if (!userName.trim()) {
      setError('Please enter a username');
      return;
    }

    setSearchLoading(true);
    setError('');
    
    console.log('Searching user:', userName);
    console.log('API URL:', process.env.REACT_APP_API_URL);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/offline-payment-form/search-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ userName: userName.trim() })
      });

      const data = await response.json();
      console.log('Search user response:', data);
      
      if (response.ok) {
        if (data.applications && data.applications.length > 0) {
          setApplications(data.applications);
          setShowApplications(true);
          setShowDetails(false);
          setShowPaymentDetails(false);
          setShowPreviousPayments(false);
        } else {
          setError('No applications found for this user');
          setShowApplications(false);
        }
      } else {
        setError(data.message || 'Failed to search user');
        setShowApplications(false);
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('Network error occurred');
      setShowApplications(false);
    } finally {
      setSearchLoading(false);
    }
  };

  const selectApplication = async (appId) => {
    setSelectedApplication(appId);
    setLoading(true);
    setError('');

    console.log('Selecting application:', appId, 'for user:', userName);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/offline-payment-form/get-application-details`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          userName: userName.trim(),
          appId: appId
        })
      });

      const data = await response.json();
      console.log('Application details response:', data);
      
      if (response.ok) {
        const newLabDetails = {
          userName: data.laboratoryName,
          category: data.categoryName,
          orgId: data.orgId,
          appId: data.appId,
          labTypeId: data.labTypeId,
          assessmentTypeId: data.assessmentTypeId
        };

        console.log('Setting lab details:', newLabDetails);
        
        setLabDetails(newLabDetails);
        setShowDetails(true);
        loadPaymentTypes(data.labTypeId);
        loadPreviousPayments(userName.trim());
      } else {
        setError(data.message || 'Failed to load application details');
      }
    } catch (error) {
      console.error('Application details error:', error);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentTypes = async (labTypeId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/offline-payment-form/get-payment-types`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ labTypeId })
      });

      const data = await response.json();
      
      if (response.ok) {
        setPaymentTypes(data.paymentTypes || []);
      } else {
        console.error('Failed to load payment types:', data.message);
      }
    } catch (error) {
      console.error('Payment types error:', error);
    }
  };

  const loadPreviousPayments = async (userName) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/offline-payment-form/get-previous-payments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ userName })
      });

      const data = await response.json();
      
      if (response.ok) {
        setPreviousPayments(data.payments || []);
        setShowPreviousPayments(data.payments && data.payments.length > 0);
      } else {
        console.error('Failed to load previous payments:', data.message);
      }
    } catch (error) {
      console.error('Previous payments error:', error);
    }
  };

  const handlePaymentTypeChange = async (paymentTypeId) => {
    console.log('🔄 Payment Type Changed:', paymentTypeId);
    
    setSelectedPaymentType(paymentTypeId);
    setLoading(true);
    setError('');
    setPaymentAlreadyMade(false);
    setPaymentAlreadyMadeDetails(null);
    setShowPaymentDetails(false);

    // Debug logging
    console.log('📊 Payment Type Change - Debug Info:');
    console.log('paymentTypeId:', paymentTypeId);
    console.log('labDetails:', labDetails);
    console.log('orgId:', labDetails.orgId);
    console.log('appId:', labDetails.appId);
    console.log('labTypeId:', labDetails.labTypeId);
    console.log('assessmentTypeId:', labDetails.assessmentTypeId);

    // Validation check
    if (!paymentTypeId || !labDetails.orgId || !labDetails.appId || !labDetails.labTypeId) {
      const missingFields = [];
      if (!paymentTypeId) missingFields.push('paymentTypeId');
      if (!labDetails.orgId) missingFields.push('orgId');
      if (!labDetails.appId) missingFields.push('appId');
      if (!labDetails.labTypeId) missingFields.push('labTypeId');
      
      const errorMessage = `Missing required fields: ${missingFields.join(', ')}`;
      console.log('❌ Validation Error:', errorMessage);
      setError(errorMessage);
      setLoading(false);
      return;
    }

    try {
      console.log('🔍 Starting payment type validation...');
      
      // Validate payment type selection before proceeding
      const validationResult = await validatePaymentType(paymentTypeId);
      console.log('✅ Validation Result:', validationResult);
      
      if (!validationResult.isValid) {
        console.log('❌ Validation Failed:', validationResult.message);
        
        // Check if this is a "payment already made" scenario
        const isAlreadyPaid = validationResult.message.toLowerCase().includes('already') || 
                            validationResult.message.toLowerCase().includes('paid') ||
                            validationResult.message.toLowerCase().includes('exists');
        
        if (isAlreadyPaid) {
          setPaymentAlreadyMade(true);
          setPaymentAlreadyMadeDetails({
            message: validationResult.message,
            paymentType: paymentTypes.find(pt => pt.id == paymentTypeId)?.name || 'Selected Payment Type'
          });
          
          // Load previous payments to show existing payment details
          await loadPreviousPayments(userName || userName);
          
          // Show previous payments section
          setShowPreviousPayments(true);
        } else {
          setError(validationResult.message);
        }
        
        setLoading(false);
        return;
      }

      console.log('✅ Validation passed, calculating fees...');

      const requestBody = {
        paymentTypeId: parseInt(paymentTypeId),
        orgId: parseInt(labDetails.orgId),
        appId: parseInt(labDetails.appId),
        labTypeId: parseInt(labDetails.labTypeId),
        assessmentTypeId: labDetails.assessmentTypeId ? parseInt(labDetails.assessmentTypeId) : null
      };

      console.log('📤 Calculate Fees Request:', requestBody);

      // Use fallback URL if REACT_APP_API_URL is not set
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const calculateUrl = `${apiUrl}/accounts/offline-payment-form/calculate-fees`;

      const response = await fetch(calculateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('📥 Calculate fees response:', data);
      
      if (response.ok) {
        setPaymentDetails(prev => ({
          ...prev,
          amount:data.amount || 0,
          totalAmount: data.totalAmount || 0,
          taxAmount: data.taxAmount || 0,
          tadaAmount: data.tadaAmount || 0,
          taxLabel: data.taxLabel || 'Tax:',
          grandTotal: data.grandTotal || 0
        }));

        if (data.offlineDetails) {
          setOfflineDetails(data.offlineDetails);
        }

        setShowPaymentDetails(true);
        console.log('✅ Payment details updated successfully');
      } else {
        console.log('❌ Calculate fees failed:', data.message);
        setError(data.message || 'Failed to calculate fees');
        setShowPaymentDetails(false);
      }
    } catch (error) {
      console.error('💥 Calculate fees error:', error);
      setError('Network error occurred');
      setShowPaymentDetails(false);
    } finally {
      setLoading(false);
    }
  };

  // Validation function based on ASP.NET logic
  const validatePaymentType = async (paymentTypeId) => {
    console.log('🔍 Starting validatePaymentType with paymentTypeId:', paymentTypeId);
    
    try {
      const requestBody = {
        paymentTypeId: parseInt(paymentTypeId),
        orgId: parseInt(labDetails.orgId),
        appId: parseInt(labDetails.appId),
        labTypeId: parseInt(labDetails.labTypeId),
        assessmentTypeId: labDetails.assessmentTypeId ? parseInt(labDetails.assessmentTypeId) : null
      };

      // Use fallback URL if REACT_APP_API_URL is not set
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const validationUrl = `${apiUrl}/accounts/offline-payment-form/validate-payment-type`;

      console.log('📤 Validation API Request:', {
        url: validationUrl,
        method: 'POST',
        body: requestBody
      });

      const response = await fetch(validationUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        body: JSON.stringify(requestBody)
      });

      console.log('📥 Validation API Response Status:', response.status);
      console.log('📥 Validation API Response Headers:', Object.fromEntries(response.headers.entries()));
      
      const data = await response.json();
      console.log('📥 Validation API Response Data:', data);
      
      if (response.ok) {
        return {
          isValid: data.isValid,
          message: data.message || 'Validation failed'
        };
      } else {
        console.log('❌ Validation API Error:', data);
        return {
          isValid: false,
          message: data.message || 'Validation failed'
        };
      }
    } catch (error) {
      console.error('💥 Validation API Error:', error);
      console.error('Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      return {
        isValid: false,
        message: 'Network error during validation: ' + error.message
      };
    }
  };

  const handleTdsChange = (checked) => {
    setPaymentDetails(prev => {
      const tdsAmount = checked ? ((prev.amount - prev.tadaAmount) * 2 / 100) : 0;
      const grandTotal = prev.amount + prev.taxAmount - tdsAmount;
      
      return {
        ...prev,
        deductTds: checked,
        tdsAmount,
        grandTotal
      };
    });
  };

  const handlePaymentModeChange = (mode) => {
    setPaymentForm(prev => ({
      ...prev,
      paymentMode: mode
    }));
  };

  const validateForm = () => {
    const { paymentMode, orderIdChequeNo, payeeBank, amount, remarks } = paymentForm;
    
    if (!paymentMode) {
      setError('Please select a payment mode');
      return false;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return false;
    }

    if (parseFloat(amount) > paymentDetails.grandTotal) {
      setError('Amount should not exceed the grand total');
      return false;
    }

    const requiresTransaction = ![
      'TDS Recovery', 
      'Payment Through CC Avenue', 
      'Previous Adjustments'
    ].includes(paymentMode);

    if (requiresTransaction) {
      if (!orderIdChequeNo.trim()) {
        setError('Please enter order/cheque/DD number');
        return false;
      }
      if (!payeeBank.trim()) {
        setError('Please enter payee bank');
        return false;
      }
    } else {
      if (!remarks.trim()) {
        setError('Please enter remarks for this payment mode');
        return false;
      }
    }

    if (paymentDetails.deductTds && !paymentDetails.tan.trim()) {
      setError('Please enter TAN number');
      return false;
    }

    return true;
  };

  const submitPayment = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Prepare the payload according to backend expectations
      const requiresTransaction = ![
        'TDS Recovery', 
        'Payment Through CC Avenue', 
        'Previous Adjustments'
      ].includes(paymentForm.paymentMode);

      const payload = {
        // Laboratory and application details
        orgId: parseInt(labDetails.orgId),
        appId: parseInt(labDetails.appId),
        paymentTypeId: parseInt(selectedPaymentType),
        paymentbyId:parseInt(localStorage.getItem('orgId')),
        
        // Payment details (extracted from paymentForm to root level)
        mode: paymentForm.paymentMode,
        referenceNumber: requiresTransaction ? paymentForm.orderIdChequeNo : (paymentForm.remarks || 'N/A'),
        amount: parseFloat(paymentForm.amount),
        remarks: paymentForm.remarks || '',
        transactionDate: paymentForm.transactionDate ? format(paymentForm.transactionDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        
        // Additional details for context (optional)
        userName: labDetails.userName,
        category: labDetails.category,
        labTypeId: parseInt(labDetails.labTypeId),
        assessmentTypeId: labDetails.assessmentTypeId ? parseInt(labDetails.assessmentTypeId) : null,
        
        // Payment calculation details
        totalAmount: paymentDetails.totalAmount,
        amount:paymentDetails.amount,
        taxAmount: paymentDetails.taxAmount,
        grandTotal: paymentDetails.grandTotal,
        deductTds: paymentDetails.deductTds,
        tdsAmount: paymentDetails.tdsAmount,
        tan: paymentDetails.tan,
        
        // Bank details (only for transaction-based modes)
        payeeBank: requiresTransaction ? paymentForm.payeeBank : ''
      };

      console.log('Submitting payment with payload:', payload);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/accounts/offline-payment-form/submit-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      console.log('Submit payment response:', data);
      
      if (response.ok) {
        setSuccess('Payment submitted successfully!');
        // Reset form
        setUserName('');
        setSelectedApplication('');
        setSelectedPaymentType('');
        setShowApplications(false);
        setShowDetails(false);
        setShowPaymentDetails(false);
        setPaymentForm({
          paymentMode: '',
          orderIdChequeNo: '',
          payeeBank: '',
          transactionDate: new Date(),
          amount: '',
          remarks: ''
        });
      } else {
        setError(data.message || 'Failed to submit payment');
      }
    } catch (error) {
      console.error('Submit payment error:', error);
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="lg" sx={{ mt: 3, mb: 3 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 3 }}>
            <PaymentIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Offline Payment
          </Typography>
          
          <Divider sx={{ mb: 3 }} />

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* User Search Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    User Name:
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    placeholder="Enter UserName"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    disabled={searchLoading}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Button
                    variant="contained"
                    onClick={searchUser}
                    disabled={searchLoading}
                    startIcon={searchLoading ? <CircularProgress size={20} /> : <SearchIcon />}
                    fullWidth
                  >
                    Search
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Application Selection */}
          <Collapse in={showApplications}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      All Applications:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <FormControl fullWidth>
                      <InputLabel>Select Application</InputLabel>
                      <Select
                        value={selectedApplication}
                        onChange={(e) => selectApplication(e.target.value)}
                        label="Select Application"
                      >
                        <MenuItem value="">-----------Select----------</MenuItem>
                        {applications.map((app) => (
                          <MenuItem key={app.appId} value={app.appId}>
                            {app.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Collapse>

          {/* Laboratory Details */}
          <Collapse in={showDetails}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box textAlign="center" sx={{ mb: 2 }}>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    {labDetails.userName}
                  </Typography>
                  <Typography variant="subtitle1" color="primary" fontWeight="bold">
                    {labDetails.category}
                  </Typography>
                </Box>
                <Divider />
              </CardContent>
            </Card>

            {/* Offline Payment Details */}
            {offlineDetails.mode && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Mode</TableCell>
                          <TableCell>UTR Number/Virtual Account Number/HDFC payment ID</TableCell>
                          <TableCell>Transaction Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>{offlineDetails.mode}</TableCell>
                          <TableCell>{offlineDetails.refNo}</TableCell>
                          <TableCell>{offlineDetails.transactionDate}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Payment Type Selection */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1">
                      Payment Type<span style={{ color: 'red' }}>*</span>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Select Payment Type</InputLabel>
                      <Select
                        value={selectedPaymentType}
                        onChange={(e) => handlePaymentTypeChange(e.target.value)}
                        label="Select Payment Type"
                      >
                        <MenuItem value="">----Select Payment Type----</MenuItem>
                        {paymentTypes.map((type) => (
                          <MenuItem key={type.id} value={type.id}>
                            {type.description}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Collapse>

          {/* Payment Already Made Alert */}
          <Collapse in={paymentAlreadyMade}>
            <Card sx={{ mb: 3, bgcolor: '#fff3cd', border: '1px solid #ffeaa7' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PaymentIcon sx={{ color: '#856404', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: '#856404' }}>
                    Payment Already Made
                  </Typography>
                </Box>
                
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {paymentAlreadyMadeDetails?.message || 
                   `Payment for ${paymentAlreadyMadeDetails?.paymentType || 'this payment type'} has already been made for this application.`}
                </Alert>

                <Typography variant="body1" gutterBottom>
                  <strong>Selected Payment Type:</strong> {paymentAlreadyMadeDetails?.paymentType}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  You can view your previous payments below or select a different payment type if available.
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={() => {
                      setSelectedPaymentType('');
                      setPaymentAlreadyMade(false);
                      setPaymentAlreadyMadeDetails(null);
                      setShowPreviousPayments(false);
                    }}
                  >
                    Select Different Payment Type
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Collapse>

          {/* Payment Details */}
          <Collapse in={showPaymentDetails}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Details
                </Typography>

                {/* Amount Details */}
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1">Amount to Pay:</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      value={`Rs ${paymentDetails.amount}`}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>
                 <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1">TADA Amount:</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      value={`Rs ${paymentDetails.tadaAmount}`}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>

                {/* TDS Deduction */}
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1">Deduct TDS:</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={paymentDetails.deductTds}
                          onChange={(e) => handleTdsChange(e.target.checked)}
                        />
                      }
                      label="Check this to deduct TDS"
                    />
                  </Grid>
                </Grid>

                {/* TAN Input */}
                <Collapse in={paymentDetails.deductTds}>
                  <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle1">
                        Enter TAN<span style={{ color: 'red' }}>*</span>:
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        placeholder="Enter TAN"
                        value={paymentDetails.tan}
                        onChange={(e) => setPaymentDetails(prev => ({ ...prev, tan: e.target.value }))}
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>
                  </Grid>
                  
                  <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle1">TDS Amount:</Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        value={`Rs ${paymentDetails.tdsAmount.toFixed(2)}`}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </Grid>

                  <Box sx={{ mb: 2, ml: 3 }}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Note:</strong> "Applicable rate of TDS will be 2% under Section 194J being fees for Technical Services (not being professional services)"
                    </Typography>
                  </Box>
                </Collapse>

                {/* Tax Amount */}
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1">{paymentDetails.taxLabel}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      value={`Rs ${paymentDetails.taxAmount.toFixed(2)}`}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                </Grid>

                {/* Grand Total */}
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1" fontWeight="bold">Grand Total:</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      value={`Rs ${paymentDetails.grandTotal.toFixed(2)}`}
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiInputBase-input': { fontWeight: 'bold' } }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Payment Form */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Payment Information
                </Typography>

                {/* Payment Mode */}
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1">
                      Payment Mode<span style={{ color: 'red' }}>*</span>:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Select Payment Mode</InputLabel>
                      <Select
                        value={paymentForm.paymentMode}
                        onChange={(e) => handlePaymentModeChange(e.target.value)}
                        label="Select Payment Mode"
                      >
                        {paymentModes.map((mode) => (
                          <MenuItem key={mode} value={mode}>
                            {mode}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Order/Cheque/DD Number */}
                {paymentForm.paymentMode && ![
                  'TDS Recovery', 
                  'Payment Through CC Avenue', 
                  'Previous Adjustments'
                ].includes(paymentForm.paymentMode) && (
                  <>
                    <Grid container spacing={3} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle1">
                          NEFT/DD/Cheque No<span style={{ color: 'red' }}>*</span>:
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          placeholder="NEFT/DD/Cheque No"
                          value={paymentForm.orderIdChequeNo}
                          onChange={(e) => setPaymentForm(prev => ({ ...prev, orderIdChequeNo: e.target.value }))}
                          required
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle1">
                          Payee Bank<span style={{ color: 'red' }}>*</span>:
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          placeholder="Payee Bank"
                          value={paymentForm.payeeBank}
                          onChange={(e) => setPaymentForm(prev => ({ ...prev, payeeBank: e.target.value }))}
                          required
                        />
                      </Grid>
                    </Grid>

                    <Grid container spacing={3} sx={{ mb: 2 }}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle1">
                          Transaction Date<span style={{ color: 'red' }}>*</span>:
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <DatePicker
                          label="Transaction Date"
                          value={paymentForm.transactionDate}
                          onChange={(date) => setPaymentForm(prev => ({ ...prev, transactionDate: date }))}
                          maxDate={new Date()}
                          renderInput={(params) => <TextField {...params} fullWidth required />}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* Amount */}
                <Grid container spacing={3} sx={{ mb: 2 }}>
                  <Grid item xs={12} md={3}>
                    <Typography variant="subtitle1">
                      Amount<span style={{ color: 'red' }}>*</span>:
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      placeholder="Amount"
                      type="number"
                      value={paymentForm.amount}
                      onChange={(e) => setPaymentForm(prev => ({ ...prev, amount: e.target.value }))}
                      required
                    />
                  </Grid>
                </Grid>

                {/* Remarks (for specific payment modes) */}
                {paymentForm.paymentMode && [
                  'TDS Recovery', 
                  'Payment Through CC Avenue', 
                  'Previous Adjustments'
                ].includes(paymentForm.paymentMode) && (
                  <Grid container spacing={3} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle1">
                        Remarks<span style={{ color: 'red' }}>*</span>:
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Remarks"
                        value={paymentForm.remarks}
                        onChange={(e) => setPaymentForm(prev => ({ ...prev, remarks: e.target.value }))}
                        required
                      />
                    </Grid>
                  </Grid>
                )}

                {/* Submit Button */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={3}></Grid>
                  <Grid item xs={12} md={6}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={submitPayment}
                      disabled={loading}
                      startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                      size="large"
                    >
                      Save Payment
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Collapse>

          {/* Previous Payment Details */}
          <Collapse in={showPreviousPayments}>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  <ReceiptIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Previous Payment Details
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>S. No</TableCell>
                        <TableCell>Application No</TableCell>
                        <TableCell>Laboratory Name</TableCell>
                        <TableCell>Order Id</TableCell>
                        <TableCell>Fees Type</TableCell>
                        <TableCell>Payment Status</TableCell>
                        <TableCell>Payment Mode</TableCell>
                        <TableCell>GSTIN</TableCell>
                        <TableCell>Is TDS Deducted</TableCell>
                        <TableCell>TAN</TableCell>
                        <TableCell>Transaction Amount</TableCell>
                        <TableCell>Transaction Date</TableCell>
                        <TableCell>Proceed Date</TableCell>
                        <TableCell>User Name/ Lab ID</TableCell>
                        <TableCell>Lab Type</TableCell>
                        <TableCell>Contact Person</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Mobile</TableCell>
                        <TableCell>Address</TableCell>
                        <TableCell>State</TableCell>
                        <TableCell>City</TableCell>
                        <TableCell>Discipline</TableCell>
                        <TableCell>Group</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previousPayments.map((payment, index) => (
                        <TableRow key={index}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{payment.applicationNo}</TableCell>
                          <TableCell>{payment.laboratoryName}</TableCell>
                          <TableCell>{payment.orderId}</TableCell>
                          <TableCell>{payment.description}</TableCell>
                          <TableCell>{payment.orderStatus}</TableCell>
                          <TableCell>{payment.paymentMode}</TableCell>
                          <TableCell>{payment.gst}</TableCell>
                          <TableCell>{payment.deductTDS}</TableCell>
                          <TableCell>{payment.tanno}</TableCell>
                          <TableCell>{payment.amount}</TableCell>
                          <TableCell>{payment.transactionDateTime}</TableCell>
                          <TableCell>{payment.proceedDateTime}</TableCell>
                          <TableCell>{payment.loginName}</TableCell>
                          <TableCell>{payment.labType}</TableCell>
                          <TableCell>{payment.contactPerson}</TableCell>
                          <TableCell>{payment.contactEmail}</TableCell>
                          <TableCell>{payment.contactMobile}</TableCell>
                          <TableCell>{payment.address}</TableCell>
                          <TableCell>{payment.state}</TableCell>
                          <TableCell>{payment.city}</TableCell>
                          <TableCell>{payment.disciplines}</TableCell>
                          <TableCell>{payment.groups}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Collapse>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default OfflinePaymentForm;